import {
    Box,
    Button,
    Grid,
    InputLabel,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import Error from "@material-ui/icons/Error";
import HelpIcon from "@material-ui/icons/Help";
import InsertDriveFile from "@material-ui/icons/InsertDriveFile";
import { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import Config from "Config";
import React, { useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Constant } from "../../Constant";
import { send } from "../../SendRequest";
import {
    getUserAgent,
    isEmpty,
    isNotEmpty,
    isObjectNotEmpty,
    parseIntFormId,
} from "../../Utils";
import CustomTooltip from "../../component/CustomTooltip/CustomTooltip";
import ErrorMsg from "../../component/ErrorMsg/ErrorMsg";
import { addAttachment } from "../../redux/action";
import { getFormId, getToken, getUserSCRN } from "../../redux/selector";
import AlertDialog from "../AlertDialog/AlertDialog";
import { Downloadable } from "../Downloadable/Downloadable";
import { DownloadButton } from "./DownloadButton";
const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: "#eee",
        textAlign: "center",
        cursor: "pointer",
        color: "#333",
        width: "500px",
        padding: "10px",
        border: `1px solid ${theme.palette.primary.main}`,
        "&:hover": {
            backgroundColor: `${theme.palette.primary.grey}`,
        },

        [theme.breakpoints.down("md")]: {
            width: "100%",
        },
    },
    icon: {
        marginTop: "16px",
        color: "#888888",
        fontSize: "42px",
    },
    deleteButton: {
        display: "flex",
    },
    button: {
        textTransform: "initial",
    },
    fileRejected: {
        color: "#FF0000",
        wordBreak: "break-all",
    },
    breakAll: {
        wordBreak: "break-all",
    },
    text: {
        fontSize: "12px",
    },
    mobileButton: {
        padding: "5px 12px",
        minWidth: "34px",
        fontSize: "0.635rem",
        "& .MuiButton-startIcon": {
            marginRight: "3px",
        },
        "& .MuiSvgIcon-root": {
            width: "0.7em",
            height: "0.7em",
        },
    },
}));

const HtmlTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: 500,
        fontSize: "12px",
    },
}));

export default function FileInput({
    control,
    name,
    fileDisabled = false,
    maxFiles = 1,
    maxSize = 3145728,
    fileType = ".pdf, .jpeg, .jpg, .png",
    disabled = false,
    totalMaxSize = maxFiles * maxSize,
    label = "",
    required = true,
    tooltip,
    style = {},
    labelStyle = {},
    showError = false,
    showDeleteBtn = true,
    isMulRowfileRejection = false,
    fileUploadMsg = "",
    signerName = "",
    signedAttachmentId = "",
    uploadHandle = () => {},
    totalMaxFiles = "",
}) {
    const { t, i18n } = useTranslation();
    const [isExceedTotalMaxSize, setIsExceedTotalMaxSize] = useState(false);
    const [isExceedMaxSize, setIsExceedMaxSize] = useState(false);
    const [isShowErrorMessage, setIsShowErrorMessage] = useState(false);
    const [errorMsg, setErrorMessage] = useState(" ");

    const formId = useSelector(getFormId());
    const token = useSelector(getToken());
    const dispatch = useDispatch();
    const scrn = useSelector(getUserSCRN());
    const [open, setOpen] = useState(false);

    const styles = useStyles();
    const { field, fieldState } = useController({
        name,
        control,
        defaultValue: [],
    });
    const childRef = useRef();
    const fileTypeTooltip = fileType.replace(/\./g, "");
    const maxLength = formId === "Form105" ? 60 : 70;
    const isMobile = isNotEmpty(getUserAgent()) && getUserAgent() !== "Firefox";

    function nameLengthValidator(file) {
        if (file.name.length > maxLength) {
            return { code: "name-too-long" };
        }
        return null;
    }

    const { fileRejections, getRootProps, getInputProps } = useDropzone({
        accept: fileType,
        maxFiles: maxFiles,
        maxSize: maxSize,
        validator: nameLengthValidator,
        onDropAccepted: (files) => {
            const fileTotalSize = files.reduce(
                (prev, current) => prev + current.size,
                0
            );
            const oldFileTotalSize = field?.value?.reduce(
                (prev, current) => prev + current.attachFileSize,
                0
            );
            const fileSize = files.length;
            const oldFileSize = field?.value?.length;
            if (fileSize + oldFileSize > maxFiles) {
                setIsExceedMaxSize(true);
            } else {
                setIsExceedMaxSize(false);
                if (fileTotalSize + oldFileTotalSize > totalMaxSize) {
                    setIsExceedTotalMaxSize(true);
                } else {
                    setIsExceedTotalMaxSize(false);

                    uploadFiles(files);
                }
            }
        },
    });

    const fileRejectionItems = fileRejections.map(({ file, errors }) => {
        console.log("errors:", errors);
        return isMulRowfileRejection ? (
            <>
                <ListItem key={file.path} style={{ padding: 0 }}>
                    <Grid container spacing={1}>
                        <Grid item xs={1}>
                            <Error />
                        </Grid>
                        <Grid
                            className={styles.fileRejected}
                            item
                            xs={10}
                            style={{ marginLeft: "5px" }}
                        >
                            {t("errorMsg.file.rejected", {
                                fileName: file.path,
                            })}
                        </Grid>
                    </Grid>
                </ListItem>
                <ListItem
                    className={styles.fileRejected}
                    style={{ marginLeft: "5px" }}
                >
                    {t("errorMsg.file.rejectedReason") +
                        t(`errorMsg.file.${errors[0].code}`, {
                            maxSize: maxSize / 1024 / 1024,
                            fileType,
                            maxFiles,
                            maxLength,
                        })}
                </ListItem>
            </>
        ) : (
            <ListItem key={file.path}>
                <Grid container spacing={1}>
                    <Grid item xs={1}>
                        <Error />
                    </Grid>
                    <Grid className={styles.fileRejected} item xs={8}>
                        {t("errorMsg.file.rejected", { fileName: file.path })}
                    </Grid>
                    <Grid className={styles.fileRejected} item xs>
                        {t("errorMsg.file.rejectedReason") +
                            t(`errorMsg.file.${errors[0].code}`, {
                                maxSize: maxSize / 1024 / 1024,
                                fileType,
                                maxFiles,
                                maxLength,
                            })}
                    </Grid>
                </Grid>
            </ListItem>
        );
    });

    const deleteFile = (fileIndex, fileId) => () => {
        // const option = {
        //     url: `${Config.CONTENT_PATH}/attachment/remove/${fileId}`,
        //     method: "DELETE",
        // };
        // dispatch(send("DELETE_ATTACHMENT", option))
        //     .then((response) => response.json())
        //     .then((jsonObj) => {
        //         if (isNotEmpty(jsonObj.id)) {
        //             dispatch({
        //                 type: Constant.action.DELETE_ATTACHMENT_DONE,
        //                 payload: { fileId: jsonObj.id },
        //             });
        //             // for display
        //             const newFiles = [...field.value];
        //             newFiles.splice(fileIndex, 1);
        //             field.onChange(newFiles);
        //         }
        //     });
        dispatch({
            type: Constant.action.DELETE_ATTACHMENT_DONE,
            payload: { fileId: fileId },
        });
        // for display
        const newFiles = [...field.value];
        newFiles.splice(fileIndex, 1);
        field.onChange(newFiles);
    };

    const exceedTotalMaxSizeError = (
        <ListItem>
            <Grid container spacing={1}>
                <Grid item xs={1}>
                    <Error />
                </Grid>
                <Grid className={styles.fileRejected} item xs={8}>
                    {t("errorMsg.file.rejected", {
                        fileName: "",
                    })}
                </Grid>
                <Grid className={styles.fileRejected} item xs>
                    {t("errorMsg.file.rejectedReason") +
                        t("errorMsg.file.exceedTotalSize", {
                            totalMaxSize: totalMaxSize / 1024 / 1024,
                        })}
                </Grid>
            </Grid>
        </ListItem>
    );

    const exceedMaxSizeError = (
        <ListItem>
            <Grid container spacing={1}>
                <Grid item xs={1}>
                    <Error />
                </Grid>
                <Grid className={styles.fileRejected} item xs={8}>
                    {t("errorMsg.file.rejected", {
                        fileName: "",
                    })}
                </Grid>
                <Grid className={styles.fileRejected} item xs>
                    {t("errorMsg.file.rejectedReason") +
                        t("errorMsg.file.too-many-files", {
                            maxFiles: maxFiles,
                        })}
                </Grid>
            </Grid>
        </ListItem>
    );

    const oncloseErrorPopup = () => setIsShowErrorMessage(false);

    const uploadFiles = (files) => {
        const promiseArr = files.map((file) => {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("form_id", parseIntFormId(formId));
            formData.append("field_name", field.name);
            formData.append("scrn", scrn || null);
            const option = {
                url: `${Config.CONTENT_PATH}/attachment/`,
                method: "POST",
                body: formData,
            };
            return dispatch(send("SUBMIT_ATTACHMENT", option))
                .then((response) => response.json())
                .then((jsonObj) => {
                    if (isNotEmpty(jsonObj.id)) {
                        dispatch(addAttachment({ [jsonObj.id]: file }));
                        return {
                            id: jsonObj.id,
                            attachFileName: jsonObj.attachFileName,
                            attachFileSize: jsonObj.attachFileSize,
                            fieldName: jsonObj.fieldName,
                        };
                    } else if (isNotEmpty(jsonObj.code)) {
                        setIsShowErrorMessage(true);
                        setErrorMessage(jsonObj.message);
                    } else {
                        return null;
                    }
                });
        });
        Promise.all(promiseArr)
            .then((resArr) => {
                const uploadFilesResult = resArr.filter((obj) =>
                    isObjectNotEmpty(obj)
                );
                field.onChange([...field.value, ...uploadFilesResult]);
                uploadHandle();
            })
            .catch((e) => {
                console.log(e);
            });
    };

    if (isEmpty(field.value)) {
        field.value = [];
    }
    return (
        <>
            {maxFiles > field.value.length && (
                <React.Fragment>
                    <Box style={style}>
                        {isNotEmpty(label) && (
                            <>
                                <InputLabel
                                    mb={2}
                                    style={labelStyle}
                                    for={name}
                                >
                                    {label}
                                    {i18n.language === "en" ? ":" : "："}
                                    {required && (
                                        <Box
                                            component="span"
                                            className="danger"
                                        >
                                            *
                                        </Box>
                                    )}
                                </InputLabel>

                                {tooltip && (
                                    <>
                                        <CustomTooltip title={tooltip} />
                                    </>
                                )}
                            </>
                        )}
                    </Box>
                    <Button
                        disabled={disabled}
                        variant="outlined"
                        className={styles.button}
                        {...getRootProps()}
                        aria-labelledby={name}
                    >
                        <input
                            {...getInputProps()}
                            id={name}
                            name={name}
                            disabled={fileDisabled}
                        />
                        <p>{fileUploadMsg || t("common.fileUploadMsg")}</p>
                    </Button>
                    <HtmlTooltip
                        disableHoverListener={true}
                        // onClose={() => {
                        //     setOpen(false);
                        // }}
                        open={open}
                        title={
                            <React.Fragment>
                                {formId === "Form105"
                                    ? t("Form105.AttachmentInstruction1", {
                                          types: fileTypeTooltip,
                                      })
                                    : t("common.AttachmentInstruction1", {
                                          size: maxSize / 1024 / 1024,
                                      })}
                                <br></br>
                                {formId === "Form105"
                                    ? t("Form105.AttachmentInstruction2", {
                                          size: maxSize / 1024 / 1024,
                                      })
                                    : t("common.AttachmentInstruction2", {
                                          types: fileTypeTooltip,
                                      })}
                                <br></br>
                                {formId === "Form105" && (
                                    <>
                                        {t("Form105.AttachmentInstruction5", {
                                            length: maxLength,
                                        })}
                                        <br></br>
                                    </>
                                )}
                                {formId === "Form105"
                                    ? t("Form105.AttachmentInstruction3", {
                                          length: maxLength,
                                      })
                                    : t("common.AttachmentInstruction3", {
                                          files: maxFiles,
                                      })}
                                <br></br>
                                {formId === "Form105"
                                    ? t("Form105.AttachmentInstruction4", {
                                          files: totalMaxFiles,
                                      })
                                    : t("common.AttachmentInstruction4", {
                                          totalMaxSize:
                                              totalMaxSize / 1024 / 1024,
                                      })}
                                <br></br>
                                {formId !== "Form105" &&
                                    t("common.AttachmentInstruction5", {
                                        length: maxLength,
                                    })}
                            </React.Fragment>
                        }
                    >
                        <Button
                            onClick={() => {
                                setOpen(!open);
                            }}
                            style={{ textTransform: "none" }}
                        >
                            <HelpIcon fontSize="small" />
                            <span>{t("common.uploadRules")}</span>
                        </Button>
                    </HtmlTooltip>
                </React.Fragment>
            )}
            {/* <form action="/action_page.php">
                <input type="file" id="myFile" name="filename"/>
                <input type="submit"/>
            </form> */}
            {field?.value.length > 0 && (
                <List>
                    {field?.value?.map((file, index) => {
                        return (
                            // <div className={styles.deleteButton} key={index}>
                            <>
                                <ListItem>
                                    <ListItemIcon style={{ minWidth: "30px" }}>
                                        <InsertDriveFile />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Box>
                                                <Downloadable
                                                    fileName={
                                                        file.attachFileName
                                                    }
                                                    fileId={
                                                        signedAttachmentId
                                                            ? signedAttachmentId
                                                            : file.id
                                                    }
                                                    id={
                                                        signedAttachmentId
                                                            ? signedAttachmentId
                                                            : file.id
                                                    }
                                                />
                                            </Box>
                                        }
                                        className={styles.breakAll}
                                        style={{ flex: 1 }}
                                    />
                                    {isNotEmpty(signerName) && (
                                        <ListItemText
                                            primary={
                                                <Box
                                                    style={{
                                                        textAlign: "right",
                                                    }}
                                                >
                                                    <Typography>
                                                        {t(
                                                            "common.iamsmart.signing.signedBy",
                                                            {
                                                                signerName,
                                                            }
                                                        )}
                                                    </Typography>
                                                </Box>
                                            }
                                            style={{ flex: 1 }}
                                        />
                                    )}
                                </ListItem>
                                <Box>
                                    {showDeleteBtn && (
                                        <DownloadButton
                                            fileName={file.attachFileName}
                                            fileId={file.id}
                                            id={file.id}
                                            buttonClass={
                                                isMobile
                                                    ? styles.mobileButton
                                                    : ""
                                            }
                                        />
                                    )}
                                    {showDeleteBtn && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            className={
                                                isMobile
                                                    ? styles.mobileButton
                                                    : "removeBtn"
                                            }
                                            onClick={deleteFile(index, file.id)}
                                            startIcon={<DeleteIcon />}
                                        >
                                            {t(`common.remove`)}
                                        </Button>
                                        // <IconButton
                                        //     aria-label="delete"
                                        //     onClick={deleteFile(index, file.id)}
                                        // >
                                        //     <DeleteIcon />
                                        // </IconButton>
                                    )}
                                </Box>
                            </>
                            // </div>
                        );
                    })}
                </List>
            )}
            {(fileRejectionItems.length > 0 ||
                isExceedTotalMaxSize ||
                exceedMaxSizeError) && (
                <List>
                    {fileRejectionItems}
                    {isExceedTotalMaxSize && exceedTotalMaxSizeError}
                    {isExceedMaxSize && exceedMaxSizeError}
                </List>
            )}
            {showError && <ErrorMsg message={fieldState?.error?.message} />}

            <AlertDialog
                formOpen={isShowErrorMessage}
                content={t(errorMsg)}
                confirmButton={{
                    action: () => oncloseErrorPopup(),
                    label: t("common.portal.close"),
                }}
            />
        </>
    );
}
